import React, { useState, useEffect } from 'react';
import { dgiiApi } from '../../api/dgii';

const AlertasAPI = () => {
	const [alertas, setAlertas] = useState([]);
	const [resumen, setResumen] = useState(null);
	const [loading, setLoading] = useState(true);
	const [expandedAlerta, setExpandedAlerta] = useState(null);

	useEffect(() => {
		cargarAlertas();
		// Recargar alertas cada 2 minutos (120000ms) solo si la ventana está visible
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				cargarAlertas();
			}
		};
		
		// Solo hacer polling si la ventana está visible
		const interval = setInterval(() => {
			if (!document.hidden) {
				cargarAlertas();
			}
		}, 120000); // 2 minutos en lugar de 30 segundos
		
		document.addEventListener('visibilitychange', handleVisibilityChange);
		
		return () => {
			clearInterval(interval);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);

	const cargarAlertas = async () => {
		try {
			setLoading(true);
			const [alertasData, resumenData] = await Promise.all([
				dgiiApi.getAlertas(),
				dgiiApi.getResumenAlertas()
			]);
			setAlertas(alertasData.results || alertasData || []);
			setResumen(resumenData);
		} catch (error) {
			console.error('Error cargando alertas:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleResolverAlerta = async (id) => {
		try {
			await dgiiApi.resolverAlerta(id);
			cargarAlertas(); // Recargar después de resolver
		} catch (error) {
			console.error('Error resolviendo alerta:', error);
			alert('Error al marcar la alerta como resuelta');
		}
	};

	const getSeveridadColor = (severidad) => {
		switch (severidad) {
			case 'critica':
				return 'bg-red-600 text-white';
			case 'alta':
				return 'bg-orange-500 text-white';
			case 'media':
				return 'bg-yellow-500 text-white';
			case 'baja':
				return 'bg-blue-500 text-white';
			default:
				return 'bg-gray-500 text-white';
		}
	};

	const getTipoIcono = (tipo) => {
		switch (tipo) {
			case 'google_vision':
				return '👁️';
			case 'n8n':
				return '⚙️';
			default:
				return '⚠️';
		}
	};

	const getTipoNombre = (tipo) => {
		switch (tipo) {
			case 'google_vision':
				return 'Google Vision API';
			case 'n8n':
				return 'n8n Workflow';
			default:
				return tipo;
		}
	};

	if (loading && alertas.length === 0) {
		return (
			<div className="bg-white rounded-lg shadow p-4">
				<div className="animate-pulse">
					<div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
					<div className="h-20 bg-gray-100 rounded"></div>
				</div>
			</div>
		);
	}

	if (!resumen || resumen.total === 0) {
		return null; // No mostrar nada si no hay alertas
	}

	return (
		<div className="mb-6 space-y-3">
			{/* Banner de alertas críticas */}
			{resumen.criticas && resumen.criticas.length > 0 && (
				<div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-r-lg shadow-md">
					<div className="flex items-start justify-between">
						<div className="flex items-start">
							<div className="text-2xl mr-3">🚨</div>
							<div className="flex-1">
								<h3 className="text-lg font-bold text-red-900 mb-1">
									Alertas Críticas Activas
								</h3>
								<p className="text-sm text-red-800 mb-2">
									Hay {resumen.criticas.length} alerta(s) crítica(s) que requieren atención inmediata
								</p>
								<div className="space-y-2">
									{resumen.criticas.slice(0, 2).map((alerta) => (
										<div key={alerta.id} className="bg-white rounded p-2 border border-red-200">
											<div className="flex items-center justify-between">
												<div className="flex items-center">
													<span className="mr-2">{getTipoIcono(alerta.tipo)}</span>
													<span className="font-semibold text-sm">{alerta.titulo}</span>
												</div>
												<button
													onClick={() => handleResolverAlerta(alerta.id)}
													className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
												>
													Marcar como resuelta
												</button>
											</div>
											<p className="text-xs text-gray-600 mt-1">{alerta.mensaje}</p>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Lista completa de alertas */}
			{alertas.length > 0 && (
				<div className="bg-white rounded-lg shadow-md border border-gray-200">
					<div className="p-4 border-b border-gray-200">
						<div className="flex items-center justify-between">
							<h3 className="text-lg font-bold text-gray-900 flex items-center">
								<span className="mr-2">⚠️</span>
								Alertas de APIs ({resumen?.total || alertas.length})
							</h3>
							<button
								onClick={cargarAlertas}
								className="text-sm text-blue-600 hover:text-blue-800"
							>
								🔄 Actualizar
							</button>
						</div>
						{resumen && (
							<div className="mt-2 flex gap-4 text-sm">
								{resumen.por_tipo && Object.entries(resumen.por_tipo).map(([tipo, count]) => (
									<span key={tipo} className="text-gray-600">
										{getTipoNombre(tipo)}: <span className="font-semibold">{count}</span>
									</span>
								))}
							</div>
						)}
					</div>
					
					<div className="divide-y divide-gray-200">
						{alertas.map((alerta) => (
							<div
								key={alerta.id}
								className={`p-4 hover:bg-gray-50 transition-colors ${
									alerta.severidad === 'critica' ? 'bg-red-50' : ''
								}`}
							>
								<div className="flex items-start justify-between">
									<div className="flex-1">
										<div className="flex items-center gap-2 mb-2">
											<span className="text-xl">{getTipoIcono(alerta.tipo)}</span>
											<span className={`px-2 py-1 rounded text-xs font-semibold ${getSeveridadColor(alerta.severidad)}`}>
												{alerta.severidad.toUpperCase()}
											</span>
											<span className="text-sm text-gray-600">
												{getTipoNombre(alerta.tipo)}
											</span>
											{alerta.contador_ocurrencias > 1 && (
												<span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
													{alerta.contador_ocurrencias} veces
												</span>
											)}
										</div>
										<h4 className="font-semibold text-gray-900 mb-1">{alerta.titulo}</h4>
										<p className="text-sm text-gray-700 mb-2">{alerta.mensaje}</p>
										
										{expandedAlerta === alerta.id && alerta.detalles_tecnicos && (
											<div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
												<h5 className="text-xs font-semibold text-gray-700 mb-2">Detalles Técnicos:</h5>
												<pre className="text-xs text-gray-600 whitespace-pre-wrap">
													{JSON.stringify(alerta.detalles_tecnicos, null, 2)}
												</pre>
											</div>
										)}
										
										<div className="flex items-center gap-2 mt-2">
											<button
												onClick={() => setExpandedAlerta(expandedAlerta === alerta.id ? null : alerta.id)}
												className="text-xs text-blue-600 hover:text-blue-800"
											>
												{expandedAlerta === alerta.id ? 'Ocultar' : 'Ver'} detalles
											</button>
											<span className="text-xs text-gray-500">
												Última ocurrencia: {new Date(alerta.ultima_ocurrencia).toLocaleString('es-DO')}
											</span>
										</div>
									</div>
									<div className="ml-4">
										<button
											onClick={() => handleResolverAlerta(alerta.id)}
											className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
										>
											✓ Resolver
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default AlertasAPI;

